/**
 * **Which kinds of task reach the trainer's queue** — `GET /attention-queue/preferences` as the
 * three cards `/zadania/rodzaje` draws (R16 to R19).
 *
 * The setting is stored per user in `AttentionPreferences` and the queue endpoint skips a muted
 * kind's detector on the server, so muting one removes it from every surface fed by the queue: this
 * screen, the Pulpit, the roster's risk lines and the phone's deck. No row means visible.
 *
 * Pure: no React, no DOM, no query hook, no i18next — the copy is mobile's
 * `clients.taskPrefs.types.<reason>.name` / `.desc`, and only the KEYS leave here.
 *
 * ── The ROWS keep the backend's order; the CARDS do not ──────────────────────────────────────
 * Within a group the response is ranked by `AttentionReasons.All`, which is also what decides
 * severity, and re-sorting there would put a second ordering in the app: a trainer comparing the
 * phone's screen with this one would find the same list in two sequences.
 *
 * The GROUPS are a different matter. Nothing in the response promises the categories arrive in any
 * particular sequence, and against the real backend they do not — the owner read Triage, Win,
 * Feedback on 19 September, where the screen had been built assuming Triage, Feedback, Win. Reading
 * the cards off the response's arrival order made the page's shape a property of a loop on the
 * server. The three cards are therefore drawn in the order declared below, which is the order the
 * canon draws them and the order they escalate in: what needs an answer, what is waiting on one,
 * what is only good news.
 *
 * ── A reason from a newer backend is IGNORED, never thrown on ────────────────────────────────
 * `attentionReasonOf` is this app's table of reasons it has strings for. A response carrying one
 * outside it is not an error — it is a backend that has shipped a kind this build predates — so the
 * row is dropped and the counter counts what is drawn. The alternative is a row titled
 * `clients.taskPrefs.types.somethingNew.name`, printed raw at the trainer.
 *
 * A CATEGORY outside the three is dropped the same way, because the group's own heading is one of
 * three keys and there is nothing honest to put over a fourth card.
 *
 * ── A kind that compares a reading carries a LEVEL (R25) ─────────────────────────────────────
 * Low sleep and low energy are judged against a level the trainer may move. The preferences read
 * states the level in force and its default; the vitals catalogue states which levels the server
 * accepts — a minimum, a maximum and a step. Both are copied, never restated: the stepper offers
 * exactly the levels the catalogue names, and a typed value outside them is refused HERE, before a
 * PUT the server would answer with a 400. A row whose catalogue entry is missing draws no control.
 *
 * ── A press is drawn before it is answered, and the catalogue is never written on a guess ─────
 * The screen draws what the SERVER holds with the presses still on their way laid over it
 * (`withPendingWrites`); the catalogue itself changes only on an answer (`withAcknowledgement`). So
 * a refused press needs no undoing — it leaves the overlay and what the server holds is what is
 * left — and three quick presses cannot be put back in the wrong order by one of them failing.
 */

import {
  attentionReasonOf,
  attentionReasonWireValue,
  type AttentionReason,
} from '~/entities/client';
import { taskGlyph, type TaskGlyph } from '~/features/attention-queue';
import type {
  TrainWithMeModulesAttentionDtosRequestsSetAttentionPreferenceRequest as SetAttentionPreferenceRequest,
  TrainWithMeModulesAttentionDtosResponsesAttentionPreferenceAcknowledgement as AttentionPreferenceAcknowledgement,
  TrainWithMeModulesAttentionDtosResponsesAttentionPreferenceItem as AttentionPreferenceItem,
  TrainWithMeModulesAttentionDtosResponsesAttentionPreferencesResponse as AttentionPreferencesResponse,
  TrainWithMeModulesVitalsDtosResponsesVitalCatalogResponse as VitalCatalogResponse,
} from '~/shared/api/generated/model';

/**
 * The three groups, the mobile key that names each, and — by DECLARATION ORDER — the order the
 * three cards stand in on the screen. Triage first, then Feedback, then Win.
 */
const GROUP_LABEL_KEY: Readonly<Record<string, string>> = {
  Triage: 'clients.taskPrefs.groups.triage',
  Feedback: 'clients.taskPrefs.groups.feedback',
  Win: 'clients.taskPrefs.groups.win',
};

/** The wire's word for a kind that reaches the queue, and for one that does not. */
export const KIND_VISIBLE = 'Visible';
export const KIND_MUTED = 'Muted';

/** The level a kind compares a reading against, and the levels the server accepts for it (R25). */
export interface KindLevel {
  /** The level in force — the trainer's own, or the default. */
  readonly value: number;
  /** The default, which „Przywróć …" returns to. */
  readonly fallback: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
}

/** One switch. */
export interface TaskKindRow {
  readonly reason: AttentionReason;
  /** The spelling the PUT carries. Never derived by upper-casing a letter — see `entities/client`. */
  readonly wireReason: string;
  readonly nameKey: string;
  readonly descKey: string;
  readonly muted: boolean;
  readonly glyph: TaskGlyph;
  /** `null` for a kind that compares nothing, and for one the catalogue did not describe. */
  readonly level: KindLevel | null;
}

/** One card. */
export interface TaskKindGroup {
  readonly category: string;
  readonly labelKey: string;
  readonly rows: readonly TaskKindRow[];
  /** How many of this group's kinds reach the queue. */
  readonly on: number;
  readonly total: number;
}

export interface TaskKindsProjection {
  readonly groups: readonly TaskKindGroup[];
  readonly on: number;
  readonly total: number;
  /** The reasons currently muted, for the sentence R18 obliges `/zadania` to carry. */
  readonly mutedReasons: readonly AttentionReason[];
}

/**
 * The catalogue as three cards.
 *
 * `preferences` is `null` while the read is open or after it failed; the projection is then empty
 * and the screen says so through its own three states rather than drawing an empty catalogue.
 */
function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function levelOf(item: AttentionPreferenceItem, catalog: VitalCatalogResponse | null | undefined): KindLevel | null {
  const value = finite(item.threshold);
  const fallback = finite(item.defaultThreshold);
  const published = item.vitalType == null ? undefined : catalog?.types?.find((type) => type.type === item.vitalType);
  const min = finite(published?.levelMin);
  const max = finite(published?.levelMax);
  const step = finite(published?.levelStep);
  if (value === null || fallback === null || min === null || max === null || step === null || step <= 0) return null;
  return { value, fallback, min, max, step };
}

/**
 * The level one step below or above, or `null` at the end of the range.
 *
 * Counted in whole STEPS from the minimum rather than by adding the step to the value: half-hour
 * steps are exact in binary, but the range and the step are the server's and a tenth is not.
 */
export function steppedLevel(level: KindLevel, direction: -1 | 1): number | null {
  const steps = Math.round((level.max - level.min) / level.step);
  const at = Math.round((level.value - level.min) / level.step) + direction;
  return at < 0 || at > steps ? null : level.min + at * level.step;
}

/**
 * A level the trainer TYPED, or `null` where it is not one the server offers — outside the range,
 * between two steps, or not a number. Either decimal separator is read: the field prints the
 * reader's own, and a trainer on a Polish keyboard still reaches for the dot. A unit left after the
 * number is ignored, because the field itself prints one.
 */
export function typedLevel(text: string, level: KindLevel): number | null {
  const cleaned = text
    .trim()
    .replace(/\s*\p{L}+$/u, '')
    .replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return null;
  const value = Number(cleaned);
  if (value < level.min || value > level.max) return null;
  const steps = (value - level.min) / level.step;
  return Math.abs(steps - Math.round(steps)) < 1e-9 ? value : null;
}

export function taskKinds(
  preferences: AttentionPreferencesResponse | null | undefined,
  catalog?: VitalCatalogResponse | null,
): TaskKindsProjection {
  const groups = new Map<string, TaskKindRow[]>();
  const mutedReasons: AttentionReason[] = [];
  let on = 0;
  let total = 0;

  for (const item of preferences?.items ?? []) {
    const category = item.category ?? '';
    if (!Object.hasOwn(GROUP_LABEL_KEY, category)) continue;
    const reason = attentionReasonOf(item.reason);
    if (reason === null) continue;

    const muted = item.state === KIND_MUTED;
    if (muted) mutedReasons.push(reason);
    else on += 1;
    total += 1;

    const rows = groups.get(category);
    const row: TaskKindRow = {
      reason,
      wireReason: attentionReasonWireValue(reason),
      nameKey: `${preferenceCopyScope(reason)}.name`,
      descKey: `${preferenceCopyScope(reason)}.desc`,
      muted,
      glyph: taskGlyph(reason),
      level: levelOf(item, catalog),
    };
    if (rows === undefined) groups.set(category, [row]);
    else rows.push(row);
  }

  return {
    groups: Object.keys(GROUP_LABEL_KEY).flatMap((category) => {
      const rows = groups.get(category);
      // A category the response never mentioned draws no card at all, rather than an empty one.
      if (rows === undefined) return [];
      return [
        {
          category,
          labelKey: GROUP_LABEL_KEY[category] ?? '',
          rows,
          on: rows.filter((row) => !row.muted).length,
          total: rows.length,
        },
      ];
    }),
    on,
    total,
    mutedReasons,
  };
}

/**
 * Where a kind's name and description are worded. Every reason the phone knew when its bundle was
 * copied has its words there; the public form's lead is a web-only reason (public-form R20), and
 * the first generation — low sleep, low energy, the record — is worded here so this screen does not
 * wait on a re-sync of that bundle.
 */
function preferenceCopyScope(reason: AttentionReason): string {
  switch (reason) {
    case 'publicFormLeadSubmitted':
      return 'web:publicForm.leads.attention.preference';
    case 'lowSleep':
    case 'lowEnergy':
    case 'prAchieved':
      return `web:tasks.kinds.types.${reason}`;
    default:
      return `clients.taskPrefs.types.${reason}`;
  }
}

/** What a switch's press sends. */
export function kindStateFor(muted: boolean): string {
  return muted ? KIND_MUTED : KIND_VISIBLE;
}

/** What one press asks for: the switch, a level, or the default level back. */
export type KindChange =
  | { readonly kind: 'mute'; readonly muted: boolean }
  | { readonly kind: 'level'; readonly threshold: number }
  | { readonly kind: 'resetLevel' };

/** One press on one row. */
export interface KindWrite {
  /** The row's reason as the PUT spells it. */
  readonly wireReason: string;
  readonly change: KindChange;
}

/**
 * The PUT one press sends, and each names ONE thing (R25).
 *
 * The switch names the state and no level, so muting can never move one. A level and a reset name
 * the level and NO state — the server keeps the state it holds — so a step pressed on a row whose
 * switch is still on its way cannot put the kind back on, or off.
 */
export function kindWriteRequest({ wireReason, change }: KindWrite): SetAttentionPreferenceRequest {
  switch (change.kind) {
    case 'mute':
      return { reason: wireReason, state: kindStateFor(change.muted) };
    case 'level':
      return { reason: wireReason, threshold: change.threshold };
    case 'resetLevel':
      return { reason: wireReason, resetThreshold: true };
  }
}

/** One row as it will stand once the press is accepted. */
function changed(item: AttentionPreferenceItem, change: KindChange): AttentionPreferenceItem {
  switch (change.kind) {
    case 'mute':
      return { ...item, state: kindStateFor(change.muted) };
    case 'level':
      return { ...item, threshold: change.threshold };
    case 'resetLevel':
      return { ...item, threshold: item.defaultThreshold };
  }
}

function withItem(
  preferences: AttentionPreferencesResponse | undefined,
  wireReason: string,
  next: (item: AttentionPreferenceItem) => AttentionPreferenceItem,
): AttentionPreferencesResponse | undefined {
  if (preferences === undefined) return preferences;
  return {
    ...preferences,
    items: (preferences.items ?? []).map((item) => (item.reason === wireReason ? next(item) : item)),
  };
}

/**
 * The catalogue the screen DRAWS: what the server holds, with every press still on its way applied
 * over it in the order it was made. A press that is refused simply stops being in `writes`.
 */
export function withPendingWrites(
  preferences: AttentionPreferencesResponse | undefined,
  writes: readonly KindWrite[],
): AttentionPreferencesResponse | undefined {
  return writes.reduce(
    (drawn, write) => withItem(drawn, write.wireReason, (item) => changed(item, write.change)),
    preferences,
  );
}

/**
 * The catalogue once the server ANSWERED a press: the state and the level its acknowledgement
 * names. A field the acknowledgement leaves out (an older backend) is taken from the press that was
 * accepted, which is what the server then holds.
 */
export function withAcknowledgement(
  preferences: AttentionPreferencesResponse | undefined,
  write: KindWrite,
  acknowledgement: AttentionPreferenceAcknowledgement | null | undefined,
): AttentionPreferencesResponse | undefined {
  return withItem(preferences, write.wireReason, (item) => {
    const accepted = changed(item, write.change);
    return {
      ...accepted,
      state: acknowledgement?.state ?? accepted.state,
      threshold: finite(acknowledgement?.threshold) ?? accepted.threshold,
    };
  });
}

/**
 * Whether a later press on the same control has overtaken this one: a newer level for the same row,
 * or a newer position of the same switch. A level and a switch never overtake each other — each PUT
 * names only its own field.
 */
export function overtakes(later: KindWrite, earlier: KindWrite): boolean {
  return (
    later.wireReason === earlier.wireReason &&
    (later.change.kind === 'mute') === (earlier.change.kind === 'mute')
  );
}

/**
 * Which of FOUR states the kinds screen is in.
 *
 * It lives here rather than in the hook because `empty` is a DECISION and not a query fact: this
 * module drops every reason and every category the build has no words for, so a backend that
 * answers 200 with a catalogue made entirely of kinds shipped after this build produces a perfectly
 * successful read and nothing to draw. Branching on `isPending` and `isError` alone gave that a
 * titled screen over an empty scroller.
 *
 * Three states, never two: `x.length === 0` cannot tell a read still running from one that answered
 * with nothing, which is the conflation every jump and every copy defect of the September round
 * came from.
 */
export type TaskKindsStatus = 'pending' | 'error' | 'empty' | 'ready';

export function taskKindsStatus(
  read: { readonly pending: boolean; readonly failed: boolean },
  kinds: TaskKindsProjection,
): TaskKindsStatus {
  if (read.pending) return 'pending';
  if (read.failed) return 'error';
  return kinds.total === 0 ? 'empty' : 'ready';
}
